/**
 * FIELD-TABLET TIMINGS AS HISTOGRAMS — the one shape a device, the server and
 * the console all agree on.
 *
 * WHY A HISTOGRAM AND NOT A PERCENTILE. A tablet uploads one summary per hour;
 * the console asks "p95 over the last day" and "over the last week". A p95 of
 * hour 1 and a p95 of hour 2 cannot be combined into the p95 of both — there is
 * no arithmetic for it — so a device that shipped percentiles would force the
 * console to either average them (wrong, and wrong in the flattering direction)
 * or to receive every raw event (hundreds an hour from 35 sites). Counts in
 * FIXED buckets merge by addition, exactly, over any range, and cost twenty
 * integers. The price is resolution: a percentile read off these buckets is
 * the bucket's upper edge — "p95 is at most 1.5 s" — which is the precision
 * the question "which tablet is slow" needs, and no more.
 *
 * The edges are chosen for where this product's timings actually land: frame
 * times (16/33/50 ms) at the bottom, the 100–2000 ms band where a tap starts to
 * feel broken at roughly 1.5x steps, and a long tail for the cold starts and
 * camera opens that were measured at several seconds on the owner's tablet.
 *
 * NEVER REORDER OR INSERT. A stored histogram is an array of counts indexed by
 * these edges; changing them silently re-labels every row already in the
 * database. A new edge set is a new `PERF_SCHEMA_VERSION` and a migration.
 * `apps/field/src/perf/perfHistogram.ts` carries a copy (the field app does not
 * depend on this package) and `perfHistogram.test.ts` there pins the two equal.
 */
export const PERF_SCHEMA_VERSION = 1;

/** Upper bounds (inclusive), in ms. Bucket `i` counts `v <= EDGES[i]`; the
 *  last bucket (index EDGES.length) counts everything above the last edge. */
export const PERF_BUCKET_EDGES_MS = [
  16, 33, 50, 75, 100, 150, 200, 300, 500, 750, 1000, 1500, 2000, 3000, 5000, 8000, 13000, 20000, 30000,
] as const;

export const PERF_BUCKET_COUNT = PERF_BUCKET_EDGES_MS.length + 1;

/** A frame at or under this is not "slow". See apps/field/src/perf/perfVocab.ts
 *  for why 200 ms. Must be one of the edges so a count can be read exactly. */
export const PERF_SLOW_FRAME_MS = 200;

export interface PerfStat {
  /** Events counted. */
  n: number;
  /** Sum of ms — a mean is `s / n`. */
  s: number;
  /** The slowest single event, exact. */
  x: number;
  /** Counts per bucket, trailing zeros trimmed. */
  b: number[];
}

export function bucketIndex(ms: number): number {
  for (let i = 0; i < PERF_BUCKET_EDGES_MS.length; i += 1) {
    if (ms <= PERF_BUCKET_EDGES_MS[i]!) return i;
  }
  return PERF_BUCKET_EDGES_MS.length;
}

export function emptyStat(): PerfStat {
  return { n: 0, s: 0, x: 0, b: [] };
}

/** Adds `other` into `into` (mutates and returns it). Exact: that is the point. */
export function mergeStat(into: PerfStat, other: PerfStat): PerfStat {
  into.n += other.n;
  into.s += other.s;
  into.x = Math.max(into.x, other.x);
  while (into.b.length < other.b.length) into.b.push(0);
  for (let i = 0; i < other.b.length; i += 1) into.b[i] = (into.b[i] ?? 0) + (other.b[i] ?? 0);
  return into;
}

/**
 * The `q` quantile (0..1) as the upper edge of the bucket it falls in, never
 * above the observed maximum. `null` for an empty stat — no events is "not
 * measured", which is not the same as fast (docs/design-rules.md §3.3).
 */
export function percentile(stat: PerfStat, q: number): number | null {
  if (stat.n <= 0) return null;
  const rank = Math.max(1, Math.ceil(q * stat.n));
  let seen = 0;
  for (let i = 0; i < stat.b.length; i += 1) {
    seen += stat.b[i] ?? 0;
    if (seen >= rank) {
      const edge = i < PERF_BUCKET_EDGES_MS.length ? PERF_BUCKET_EDGES_MS[i]! : stat.x;
      return Math.min(edge, stat.x);
    }
  }
  return stat.x;
}

/** Events strictly above `ms`, which must be one of the edges. */
export function countAbove(stat: PerfStat, ms: number): number {
  const edge = PERF_BUCKET_EDGES_MS.indexOf(ms as (typeof PERF_BUCKET_EDGES_MS)[number]);
  if (edge < 0) throw new Error(`countAbove: ${ms} is not a bucket edge`);
  let n = 0;
  for (let i = edge + 1; i < stat.b.length; i += 1) n += stat.b[i] ?? 0;
  return n;
}
